import {
  Entity,
  RELATION_MEMBER_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityRefLink,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Divider,
  LinearProgress,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Security from '@material-ui/icons/Security';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import React from 'react';
import useAsync from 'react-use/lib/useAsync';

const RELATION_GROUP_INFO_OF = 'groupInfoOf';

type MembershipSource = 'id-manager' | 'people-app';

type MembershipRow = {
  groupRef: string;
  role?: string;
  sources: MembershipSource[];
};

type UserRole = {
  link?: unknown;
  role?: unknown;
};

type UserProfileCardProps = {
  variant?: 'gridItem' | 'fullHeight';
};

type UserProfileSpec = {
  profile?: {
    displayName?: unknown;
    email?: unknown;
    picture?: unknown;
  };
};

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  profile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  avatar: {
    height: theme.spacing(8),
    width: theme.spacing(8),
    fontSize: theme.typography.h5.fontSize,
    flexShrink: 0,
  },
  profileDetails: {
    minWidth: 0,
  },
  displayName: {
    fontWeight: 700,
  },
  metadata: {
    marginTop: theme.spacing(0.5),
    overflowWrap: 'anywhere',
  },
  memberships: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
  },
  rowMain: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75, 1),
  },
  groupLink: {
    fontWeight: 700,
  },
  role: {
    lineHeight: 1.4,
  },
  sourceIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  icon: {
    fontSize: 18,
  },
}));

const normalizeRef = (ref: string) => ref.toLocaleLowerCase('en-US');

const currentUserRef = (entity: Entity) =>
  normalizeRef(
    stringifyEntityRef({
      kind: entity.kind,
      namespace: entity.metadata.namespace ?? 'default',
      name: entity.metadata.name,
    }),
  );

const getRoleForUser = (groupInfo: Entity, userRef: string) => {
  const userRoles = Array.isArray(groupInfo.spec?.userRoles)
    ? (groupInfo.spec.userRoles as UserRole[])
    : [];

  const userRole = userRoles.find(role => {
    if (typeof role.link !== 'string') {
      return false;
    }

    return normalizeRef(role.link) === userRef;
  });

  return typeof userRole?.role === 'string' ? userRole.role : undefined;
};

const getGroupRefFromGroupInfo = (groupInfo: Entity) =>
  groupInfo.relations?.find(
    relation =>
      relation.type === RELATION_GROUP_INFO_OF &&
      normalizeRef(relation.targetRef).startsWith('group:'),
  )?.targetRef;

const addSource = (sources: MembershipSource[], source: MembershipSource) => {
  if (!sources.includes(source)) {
    sources.push(source);
  }
};

const resolveRows = async (
  entity: Entity,
  getEntityByRef: (entityRef: string) => Promise<Entity | undefined>,
): Promise<MembershipRow[]> => {
  const rowsByGroupRef = new Map<string, MembershipRow>();
  const memberOfRelations = getEntityRelations(entity, RELATION_MEMBER_OF);

  for (const relation of memberOfRelations) {
    const targetRef = stringifyEntityRef(relation);
    const normalizedTargetRef = normalizeRef(targetRef);

    if (!normalizedTargetRef.startsWith('group:')) {
      continue;
    }

    rowsByGroupRef.set(normalizedTargetRef, {
      groupRef: targetRef,
      sources: ['id-manager'],
    });
  }

  const userRef = currentUserRef(entity);
  const groupInfoRefs = memberOfRelations
    .map(relation => stringifyEntityRef(relation))
    .filter(ref => normalizeRef(ref).startsWith('groupinfo:'));

  await Promise.all(
    groupInfoRefs.map(async groupInfoRef => {
      const groupInfo = await getEntityByRef(groupInfoRef);
      if (!groupInfo) {
        return;
      }

      const groupRef = getGroupRefFromGroupInfo(groupInfo);
      if (!groupRef) {
        return;
      }

      const normalizedGroupRef = normalizeRef(groupRef);
      const existing = rowsByGroupRef.get(normalizedGroupRef) ?? {
        groupRef,
        sources: [],
      };

      addSource(existing.sources, 'people-app');
      existing.role = existing.role ?? getRoleForUser(groupInfo, userRef);
      rowsByGroupRef.set(normalizedGroupRef, existing);
    }),
  );

  return Array.from(rowsByGroupRef.values()).sort((left, right) =>
    left.groupRef.localeCompare(right.groupRef),
  );
};

const stringValue = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value : undefined;

const getProfile = (entity: Entity) => {
  const spec = entity.spec as UserProfileSpec | undefined;
  const displayName =
    stringValue(spec?.profile?.displayName) ??
    entity.metadata.title ??
    entity.metadata.name;

  return {
    displayName,
    email: stringValue(spec?.profile?.email),
    picture: stringValue(spec?.profile?.picture),
    description: stringValue(entity.metadata.description),
  };
};

const getInitials = (displayName: string) =>
  displayName
    .split(/[.\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toLocaleUpperCase('en-US'))
    .join('');

const SourceIcons = ({ sources }: { sources: MembershipSource[] }) => {
  const classes = useStyles();

  return (
    <div className={classes.sourceIcons}>
      {sources.includes('id-manager') && (
        <Tooltip title="ID-manager">
          <VerifiedUser className={classes.icon} aria-label="ID-manager" />
        </Tooltip>
      )}
      {sources.includes('people-app') && (
        <Tooltip title="PeopleApp">
          <Security className={classes.icon} aria-label="PeopleApp" />
        </Tooltip>
      )}
    </div>
  );
};

export const UserProfileCard = ({
  variant = 'gridItem',
}: UserProfileCardProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const cardVariant = variant === 'fullHeight' ? 'outlined' : undefined;
  const profile = getProfile(entity);

  const {
    loading,
    error,
    value: rows = [],
  } = useAsync(
    () =>
      resolveRows(entity, entityRef => catalogApi.getEntityByRef(entityRef)),
    [catalogApi, entity],
  );

  return (
    <Card variant={cardVariant}>
      <CardHeader title="User Profile" />
      <Divider />
      {loading && <LinearProgress />}
      <CardContent className={classes.content}>
        <div className={classes.profile}>
          <Avatar
            alt={profile.displayName}
            className={classes.avatar}
            src={profile.picture}
          >
            {getInitials(profile.displayName)}
          </Avatar>
          <div className={classes.profileDetails}>
            <Typography className={classes.displayName} variant="h6">
              {profile.displayName}
            </Typography>
            {profile.email && (
              <Typography
                className={classes.metadata}
                color="textSecondary"
                component="a"
                href={`mailto:${profile.email}`}
                variant="body2"
              >
                {profile.email}
              </Typography>
            )}
            {profile.description && (
              <Typography
                className={classes.metadata}
                color="textSecondary"
                variant="body2"
              >
                {profile.description}
              </Typography>
            )}
          </div>
        </div>
        <Divider />
        <div className={classes.memberships}>
          <Typography variant="h6">Group Memberships</Typography>
          {error && (
            <Typography color="error">
              Group memberships could not be loaded.
            </Typography>
          )}
          {!loading && !error && rows.length === 0 && (
            <Typography color="textSecondary">
              No group memberships found.
            </Typography>
          )}
          {!loading && !error && rows.length > 0 && (
            <div>
              {rows.map(row => (
                <div key={normalizeRef(row.groupRef)} className={classes.row}>
                  <div className={classes.rowMain}>
                    <EntityRefLink
                      className={classes.groupLink}
                      entityRef={row.groupRef}
                    />
                    <SourceIcons sources={row.sources} />
                    {row.role && (
                      <Typography
                        className={classes.role}
                        color="textSecondary"
                        variant="body2"
                      >
                        {row.role}
                      </Typography>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
